import { FunctionComponent, Ref } from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  Autocomplete,
  Box,
  Grid,
  TextField,
  MenuItem,
} from "@mui/material";
import { Dayjs } from 'dayjs';

import { FiltersWrapper, ClearButton, DatePickerWrapper } from "..";

type Props = {
  options: { identifiers: string[], commercialNumbers: string[], registrations: string[], departures: string[], arrivals: string[], ids: string[], tlcs: string[] };
  filters: Filters;
  isSuperAdmin?: boolean;
  operatorOptions?: Operator[];
  searchInputRef?: Ref<HTMLInputElement>;
  onSearchChange: (value: string) => void;
  onOperatorChange?: (_: any, value: Operator | null) => void;
  onDateChange: (date: Dayjs | null) => void;
  onCallsignChange: (_: any, value: string | null) => void;
  onRegistrationChange: (_: any, value: string | null) => void;
  onDepartureAirportChange: (_: any, value: string | null) => void;
  onArrivalAirportChange: (_: any, value: string | null) => void;
  onFlightIDChange: (_: any, value: string | null) => void;
  onCommercialFlightNumberChange: (_: any, value: string | null) => void;
  onTLCChange: (_: any, value: string | null) => void;
  hideSearch?: boolean;
  onPurposeFilterChange?:(patch:Partial<Filters>)=>void;
  purposeOptions?:Record<string,string>;
};

export const Filters: FunctionComponent<Props> = (props) => {
  const {
    options: { identifiers = [], commercialNumbers = [], registrations = [], departures = [], arrivals = [], ids = [], tlcs = [] },
    filters: { date, search, callsign, registration, departureAirport, arrivalAirport, flightID, commercialFlightNumber, tlc, operatorRef },
    isSuperAdmin = false,
    operatorOptions = [],
    searchInputRef,
    onSearchChange,
    onOperatorChange,
    onDateChange,
    onCallsignChange,
    onRegistrationChange,
    onDepartureAirportChange,
    onArrivalAirportChange,
    onFlightIDChange,
    onCommercialFlightNumberChange,
    onTLCChange,
    hideSearch = false,
  } = props;

  const clearDate = () => {
    onDateChange(null);
  }
  const selectedOperator = operatorOptions.find((operator) => Number(operator.id) === Number(operatorRef)) || null;
  // TODO: fix options warnings
  return (
    <FiltersWrapper>
      <Grid container spacing={1}>
        {props.onPurposeFilterChange&&<>
          <Grid item xs={12} sm={4}><TextField select fullWidth size="small" label="Flight type" value={props.filters.atcType||''} onChange={e=>props.onPurposeFilterChange?.({atcType:e.target.value||null})}><MenuItem value="">All</MenuItem>{['S','N','G','M','X'].map(code=><MenuItem key={code} value={code}>{code}</MenuItem>)}</TextField></Grid>
          <Grid item xs={12} sm={4}><TextField select fullWidth size="small" label="Flight purpose" value={props.filters.flightPurpose||''} onChange={e=>props.onPurposeFilterChange?.({flightPurpose:e.target.value||null})}><MenuItem value="">All</MenuItem>{Object.entries(props.purposeOptions||{}).map(([code,label])=><MenuItem key={code} value={code}>{label}</MenuItem>)}</TextField></Grid>
          <Grid item xs={12} sm={4}><TextField select fullWidth size="small" label="Purpose review" value={props.filters.purposeReview||''} onChange={e=>props.onPurposeFilterChange?.({purposeReview:e.target.value||null})}><MenuItem value="">All</MenuItem><MenuItem value="1">Needs review</MenuItem><MenuItem value="0">No review needed</MenuItem></TextField></Grid>
        </>}

        {!hideSearch ? <Grid item xs={12} md={isSuperAdmin ? 8 : 12}>
          <TextField
            label="Search flights"
            inputRef={searchInputRef}
            value={search || ""}
            onChange={(event) => onSearchChange(event.target.value)}
            helperText="Commercial number, flight number, ID, route, city pair, aircraft or TLC"
            size="small"
            fullWidth
          />
        </Grid> : null}
        {isSuperAdmin && onOperatorChange ? (
          <Grid item xs={12} md={4}>
            <Autocomplete
              options={operatorOptions}
              value={selectedOperator}
              onChange={onOperatorChange}
              getOptionLabel={(operator) => operator?.name || operator?.company || `Operator ${operator?.id}`}
              renderInput={(params) => <TextField {...params} label="Operator" size="small" />}
              sx={{ width: "100%" }}
            />
          </Grid>
        ) : null}
        <Grid item xs={12} sm={6} md={3} lg={1.7}>
          <Box sx={{ position: "relative" }}>
            <DatePickerWrapper>
              <DatePicker
                value={date}
                onChange={onDateChange}
                format="YYYY-MM-DD"
                sx={{ width: "100%" }}
                label="Exact date"
                slotProps={{ textField: { size: "small" } }}
              />
              <ClearButton onClick={clearDate} />
            </DatePickerWrapper>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={1.7}>
          <Autocomplete
            options={identifiers}
            value={callsign}
            onChange={onCallsignChange}
            renderInput={(params) => <TextField {...params} label="Callsign" size="small" />}
            sx={{ width: "100%" }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={1.7}>
          <Autocomplete
            options={registrations}
            value={registration}
            onChange={onRegistrationChange}
            renderInput={(params) => <TextField {...params} label="Registration" size="small" />}
            sx={{ width: "100%" }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={1.7}>
          <Autocomplete
            options={departures}
            value={departureAirport}
            onChange={onDepartureAirportChange}
            renderInput={(params) => (
              <TextField {...params} label="Departure airport" size="small" />
            )}
            sx={{ width: "100%" }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={1.7}>
          <Autocomplete
            options={arrivals}
            value={arrivalAirport}
            onChange={onArrivalAirportChange}
            renderInput={(params) => (
              <TextField {...params} label="Arrival airport" size="small" />
            )}
            sx={{ width: "100%" }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={1.7}>
          <Autocomplete
            options={commercialNumbers}
            value={commercialFlightNumber}
            onChange={onCommercialFlightNumberChange}
            renderInput={(params) => (
              <TextField {...params} label="Commercial number" size="small" />
            )}
            sx={{ width: "100%" }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={1.7}>
          <Autocomplete
            options={ids.map((id: string) => id.toString())}
            value={flightID}
            onChange={onFlightIDChange}
            renderInput={(params) => (
              <TextField {...params} label="Flight ID" size="small" />
            )}
            sx={{ width: "100%" }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={1.8}>
          <Autocomplete
            options={tlcs}
            value={tlc}
            onChange={onTLCChange}
            renderInput={(params) => (
              <TextField {...params} label="TLC" size="small" />
            )}
            sx={{ width: "100%" }}
          />
        </Grid>
      </Grid>
    </FiltersWrapper>
  );
};
